import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../utils/translations';
import api from '../utils/api';
import { MapPin, Mail, Phone } from 'lucide-react';
import PageMeta from '../components/PageMeta';

const About = () => {
  const { language } = useLanguage();
  const [content, setContent] = useState({
    history: null,
    purpose: null,
    vision: null
  });

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const [history, purpose, vision] = await Promise.all([
        api.get('/content/history').catch(() => null),
        api.get('/content/purpose').catch(() => null),
        api.get('/content/vision').catch(() => null)
      ]);
      
      setContent({
        history: history?.data,
        purpose: purpose?.data,
        vision: vision?.data
      });
    } catch (error) {
      console.error('Error fetching content:', error);
    }
  };

  const defaultContent = {
    history: {
      title_en: 'Our History',
      title_ti: 'ታሪኽና',
      content_en: 'The church ministry began in 2016 with a small family under the guidance of Egyptian fathers. From September 2017 to the end of 2018, ministry continued with a handful of deacons and families focused on Bible study and prayer. Since 2018, we have been utilizing the facilities of St. Philopatheer Coptic Orthodox Church, with leadership from their priests and fathers.',
      content_ti: 'ናይ ቤተ ክርስትያን ኣገልግሎት ኣብ 2016 ብንኡስ ስድራቤት ብመምርሒ ግብጻውያን መምህራን ተጀሚሩ። ካብ መስከረም 2017 ክሳዕ መወዳእታ 2018 ኣገልግሎት ብውሑዳት ዲያቆናትን ስድራቤታትን ብናይ መጽሓፍ ቅዱስ ትምህርትን ጸሎትን ቀጺሉ። ካብ 2018 ጀሚሩ ናይ ቅዱስ ፊሎፓቴር ኮብቲክ ኦርቶዶክስ ቤተ ክርስትያን ብምጥቃም፡ ብመምርሒ ካህናቶምን መምህራኖምን ኣገልግሎት ንገብር ኣለና።'
    },
    purpose: {
      title_en: 'Our Purpose',
      title_ti: 'ዕላማና',
      content_en: 'To prepare people worthy of the kingdom of God by preaching the gospel, converting the lost, discipling believers, and raising deacons and priests. To work in unity and love, sacrificing to save many. To provide comprehensive spiritual and social services in Tigrinya, Geez, and English.',
      content_ti: 'ንመንግስተ ኣምላኽ ብቁዓት ዝኾኑ ሰባት ንምድላው፡ ወንጌል ብምስባኽ፡ ዝጠፍኡ ብምምላስ፡ ብምምሃር፡ ዲያቆናትን ካህናትን ብምዕባይ። ብሓድነትን ፍቕርን ንምስራሕ፡ ብዙሓት ንምድሓን ምስግጋር። ብትግርኛ፡ ግእዝን እንግሊዝኛን መንፈሳውን ማሕበራውን ኣገልግሎታት ንምሃብ።'
    },
    vision: {
      title_en: 'Our Vision',
      title_ti: 'ራእይና',
      content_en: 'To have our own church building in Dallas and surrounding areas to provide comprehensive spiritual and social services. Our future church will be a gathering place for prayer, learning, fasting, and worship. It will also be a place for baptisms, weddings, and other spiritual services, accessible in familiar languages.',
      content_ti: 'ኣብ ዳላስን ኣብ ከባቢኡን ናይ ገዛእ ርእስና ቤተ ክርስትያን ሕንጻ ንምሓዝ፡ መንፈሳውን ማሕበራውን ኣገልግሎታት ንምሃብ። መጻኢ ቤተ ክርስትያንና ቦታ ጸሎት፡ ትምህርቲ፡ ጾምን ኣምልኾን ክኸውን እዩ። ከምኡ እውን ቦታ ጥምቀት፡ መርዓ፡ ከምኡ እውን ካልእ መንፈሳዊ ኣገልግሎታት፡ ብእንፈልጦ ቋንቋታት ክኸውን እዩ።'
    }
  };

  const getContent = (key) => {
    return content[key] || defaultContent[key];
  };

  return (
    <>
      <PageMeta 
        title="About Us"
        path="/about"
        description="Learn about our church history, mission, and vision. Serving the Orthodox Tewahdo community in Dallas since 2016 with spiritual guidance and fellowship."
      />
      <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="bg-gradient-to-br from-amber-100 via-orange-50 to-rose-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4" style={{fontFamily: 'Playfair Display, serif'}}>
            {t(language, 'about.title')}
          </h1>
          <p className="text-lg text-gray-700 dark:text-gray-300">
            {t(language, 'home.churchName')}
          </p>
        </div>
      </section>

      {/* History */}
      <section className="py-16 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-amber-700 dark:text-amber-500 mb-6" style={{fontFamily: 'Playfair Display, serif'}}>
            {language === 'en' ? getContent('history').title_en : getContent('history').title_ti}
          </h2>
          <div className="prose prose-lg dark:prose-invert max-w-none">
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">
              {language === 'en' ? getContent('history').content_en : getContent('history').content_ti}
            </p>
          </div>
        </div>
      </section>

      {/* Purpose */}
      <section className="py-16 bg-amber-50 dark:bg-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-amber-700 dark:text-amber-500 mb-6" style={{fontFamily: 'Playfair Display, serif'}}>
            {language === 'en' ? getContent('purpose').title_en : getContent('purpose').title_ti}
          </h2>
          <div className="prose prose-lg dark:prose-invert max-w-none">
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">
              {language === 'en' ? getContent('purpose').content_en : getContent('purpose').content_ti}
            </p>
          </div>
        </div>
      </section>

      {/* Vision */}
      <section className="py-16 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-amber-700 dark:text-amber-500 mb-6" style={{fontFamily: 'Playfair Display, serif'}}>
            {language === 'en' ? getContent('vision').title_en : getContent('vision').title_ti}
          </h2>
          <div className="prose prose-lg dark:prose-invert max-w-none">
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">
              {language === 'en' ? getContent('vision').content_en : getContent('vision').content_ti}
            </p>
          </div>
        </div>
      </section>

      {/* Youth Ministry */}
      <section className="py-16 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-gray-800 dark:to-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-amber-700 dark:text-amber-500 mb-6 text-center" style={{fontFamily: 'Playfair Display, serif'}}>
            {language === 'en' ? 'Youth Ministry - Our Top Priority' : 'መንእሰያትን ሕጻናትን ናይ ቀዳምነት አገልግሎት እዩ'}
          </h2>
          <div className="bg-white dark:bg-gray-700 rounded-2xl p-8 shadow-lg">
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
              {language === 'en' 
                ? 'Serving the youth is our top priority. Our future church will create an environment where children can learn, play, and grow together, forming long-term relationships and strong bonds.'
                : 'ናይ መንእሰያትን ሕጻናትን ናይ ቀዳማይ ቀድምነት አገልግሎትና እዩ። መጻኢ ቤተ ክርስትያንና ንደቅና ክመሃሩ፡ ክጻወቱ፡ ንሓድሕዶም  ብሕብረት ክዓብዩ ዝኽእሉ ከባቢ ክትፈጥር እያ።'
              }
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <h3 className="font-bold text-lg text-amber-800 dark:text-amber-400">
                  {language === 'en' ? '📖 Religious Education' : '📖 ሃይማኖታዊ ትምህርቲ'}
                </h3>
                <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400">
                  <li>{language === 'en' ? 'Teaching in English for children' : 'ንደቅና ብእንግሊዝኛ ምምሃር'}</li>
                  <li>{language === 'en' ? 'Learning foundational faith' : 'መሰረታዊ እምነት ምምሃር'}</li>
                  <li>{language === 'en' ? 'Liturgy and hymnody training' : 'ቅዳሴን መዝሙርን ስልጠና ምሃብ'}</li>
                </ul>
              </div>
              
              <div className="space-y-3">
                <h3 className="font-bold text-lg text-amber-800 dark:text-amber-400">
                  {language === 'en' ? '🎯 Youth Development' : '🎯 ምስልጣን መንእሰያት'}
                </h3>
                <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400">
                  <li>{language === 'en' ? 'Future church ministers program' : 'ኣግልገልቲ ምፍራይ'}</li>
                  <li>{language === 'en' ? 'Priesthood ordination plan' : 'ካህናት ድያቆናትን ምፍራይ'}</li>
                  <li>{language === 'en' ? 'Fellowship and sports activities' : 'ሕብረትን ስፖርትን ምዝውታር'}</li>
                </ul>
              </div>
              
              <div className="space-y-3">
                <h3 className="font-bold text-lg text-amber-800 dark:text-amber-400">
                  {language === 'en' ? '🏫 Facilities' : '🏫 መሳለጥያታት'}
                </h3>
                <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400">
                  <li>{language === 'en' ? 'Library and toy equipment' : 'ቤት መጽሓፍትን መጻወቲን'}</li>
                  <li>{language === 'en' ? 'Space for cultural celebrations' : 'ንባህላዊ በዓላት ቦታ'}</li>
                  <li>{language === 'en' ? 'Daily meeting rooms' : 'መዓልታዊ ናይ ኣኬባ ክፍሊ'}</li>
                </ul>
              </div>
              
              <div className="space-y-3">
                <h3 className="font-bold text-lg text-amber-800 dark:text-amber-400">
                  {language === 'en' ? '💪 Support Programs' : '💪 ደገፍ መደባት'}
                </h3>
                <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400">
                  <li>{language === 'en' ? 'Guidance to avoid harmful habits' : 'ምክሪ ሓደገኛ ልምዲ ንምውጋድ'}</li>
                  <li>{language === 'en' ? 'Help for addiction struggles' : 'ንእሱራት ሓገዝ'}</li>
                  <li>{language === 'en' ? 'Mentorship programs' : 'ናይ ምክሪ መደባት'}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-16 bg-gradient-to-br from-amber-100 to-orange-100 dark:from-gray-800 dark:to-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-amber-700 dark:text-amber-500 mb-12" style={{fontFamily: 'Playfair Display, serif'}}>
            {t(language, 'about.contactInfo')}
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white dark:bg-gray-700 rounded-xl p-6 shadow-lg text-center">
              <MapPin className="w-12 h-12 text-amber-600 dark:text-amber-500 mx-auto mb-4" />
              <h3 className="font-bold text-gray-900 dark:text-white mb-2">{t(language, 'contact.address')}</h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                8822 Angora St<br />
                Dallas, TX 75218
              </p>
            </div>
            
            <div className="bg-white dark:bg-gray-700 rounded-xl p-6 shadow-lg text-center">
              <Mail className="w-12 h-12 text-amber-600 dark:text-amber-500 mx-auto mb-4" />
              <h3 className="font-bold text-gray-900 dark:text-white mb-2">{t(language, 'contact.email')}</h3>
              <a href="mailto:mheeotcdallas@gmail.com" className="text-amber-600 dark:text-amber-400 hover:underline text-sm">
                mheeotcdallas@gmail.com
              </a>
            </div>
            
            <div className="bg-white dark:bg-gray-700 rounded-xl p-6 shadow-lg text-center">
              <Phone className="w-12 h-12 text-amber-600 dark:text-amber-500 mx-auto mb-4" />
              <h3 className="font-bold text-gray-900 dark:text-white mb-2">{t(language, 'contact.phone')}</h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                {language === 'en' ? 'Contact via email' : 'ብኢመይል ርኸቡና'}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default About;
